import { useCallback, useEffect, useRef, useState } from 'react';
import type { SupportMessage } from '@chicare/shared';

import { cn } from '@/lib/cn';
import { realtime } from '@/lib/realtime';
import { Icon } from '@/components/icons/Icon';

/**
 * The chat itself, used by both sides.
 *
 * ── WHY ONE COMPONENT FOR THE BUYER AND THE DESK ────────────────────────────
 *
 * They are the same conversation seen from opposite ends. Two copies would
 * drift within a month, and the half that drifts is always the one nobody
 * demos — which here is the desk, the side staff look at all day.
 *
 * Everything that differs between the two ends is passed in: how to load, how
 * to poll, how to send, and what to call the other party. The server decides
 * `mine` on each message, so neither end re-implements the rule.
 *
 * ── A SOCKET, WITH POLLING UNDERNEATH IT ────────────────────────────────────
 *
 * Messages and typing arrive over Reverb, so they land the instant they are
 * sent rather than on the next tick.
 *
 * The polling never went away, and that is the point. Reverb is a daemon: it
 * restarts on deploy, dies under memory pressure, and is unreachable from a
 * phone that has just dropped to 3G. A chat that goes silent in any of those
 * cases has not degraded, it has BROKEN — silently, with support looking
 * staffed and nothing arriving.
 *
 * So both run. While the socket is connected the poll drops to a slow
 * heartbeat, which costs almost nothing and catches anything a dropped frame
 * lost. The moment the socket is not connected it speeds back up and the chat
 * carries on working exactly as it did before Reverb existed.
 *
 * Everything is deduplicated by id, so a message that arrives down both routes
 * is rendered once. That dedupe is what makes running both safe.
 *
 * Polling pauses when the tab is hidden either way, so a chat left open in a
 * background tab costs nothing.
 */

/** Poll interval while the socket is NOT carrying traffic. */
const POLL_MS = 2_000;

/**
 * Poll interval while the socket IS connected.
 *
 * Not zero. A socket can report itself connected and still deliver nothing —
 * a dropped frame during a reconnect, a subscription torn down by a rebuild,
 * a proxy that closed an idle connection without telling anybody. The heartbeat
 * is what bounds how long such a silence can last.
 *
 * Five seconds rather than twenty. Twenty makes the worst case a conversation
 * that appears to have stalled for a third of a minute, which in a support
 * chat is long enough for somebody to give up and ring instead. The extra
 * requests are three an open minute, against a query that is one indexed range
 * scan.
 */
const HEARTBEAT_MS = 5_000;

/** The most often we will tell the other side that keys are going down. */
const TYPING_PING_MS = 2_500;

export interface LiveChatProps {
  /**
   * The private channel this conversation lives on, if there is one.
   *
   * Omitted — or passed when no socket is configured — and the chat runs on
   * polling alone, which is a supported state rather than a failure.
   */
  channel?: string;

  /**
   * Whose side an author is on, from THIS end.
   *
   * The server resolves `mine` per message for the REST paths, because it
   * knows who asked. A broadcast has no asker — the same frame goes to the
   * customer and to the desk — so the one thing it cannot carry is whose it
   * is. This resolves it on arrival, from the author, which is the one thing
   * the frame does carry.
   */
  isMine?: (author: SupportMessage['author']) => boolean;
  /** The conversation so far, plus whether the other side is typing. */
  load: (signal?: AbortSignal) => Promise<{
    messages: SupportMessage[];
    othersTyping: boolean;
    status: 'open' | 'closed';
  }>;
  poll: (
    after: number,
    signal?: AbortSignal,
  ) => Promise<{ messages: SupportMessage[]; othersTyping: boolean; status: 'open' | 'closed' }>;
  send: (body: string) => Promise<SupportMessage>;
  typing: () => Promise<void>;
  /** "ChiCare support", or the customer's name on the desk. */
  otherParty: string;
  placeholder?: string;
  className?: string;
  /** Shown above the composer when there is nothing yet. */
  empty?: React.ReactNode;
}

export function LiveChat({
  load,
  poll,
  send,
  typing,
  otherParty,
  channel,
  isMine,
  placeholder = 'Type your message',
  className,
  empty,
}: LiveChatProps) {
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [othersTyping, setOthersTyping] = useState(false);
  const [draft, setDraft] = useState('');
  const [ready, setReady] = useState(false);
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);

  const list = useRef<HTMLDivElement | null>(null);
  const lastTypingPing = useRef(0);

  // Whether the socket is carrying traffic. Drives the poll interval, and
  // nothing else — the chat behaves identically either way.
  const [live, setLive] = useState(false);

  // Cleared by a timer rather than by an event: the other end stops typing by
  // stopping, and there is no "stopped" frame to listen for.
  const typingOff = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // The newest id we hold. Kept in a ref as well as in state because the
  // polling loop closes over it and must always see the current value — a
  // stale cursor re-fetches messages already on screen, which is how a chat
  // starts duplicating its own history.
  const cursor = useRef(0);

  const absorb = useCallback((incoming: SupportMessage[]) => {
    if (incoming.length === 0) return;

    setMessages((current) => {
      // Deduplicated by id. A message sent optimistically and then returned by
      // the next poll is the same message, and showing it twice is the most
      // obvious possible bug in a chat.
      const seen = new Set(current.map((m) => m.id));
      const added = incoming.filter((m) => !seen.has(m.id));

      return added.length === 0 ? current : [...current, ...added];
    });

    cursor.current = Math.max(cursor.current, ...incoming.map((m) => m.id));
  }, []);

  // ── First load ───────────────────────────────────────────────────────────
  useEffect(() => {
    const controller = new AbortController();

    load(controller.signal)
      .then((data) => {
        setMessages(data.messages);
        setOthersTyping(data.othersTyping);
        cursor.current = data.messages.reduce((max, m) => Math.max(max, m.id), 0);
        setReady(true);
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setFailed(true);
          setReady(true);
        }
      });

    return () => controller.abort();
  }, [load]);

  // ── The socket ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!channel) return;

    const client = realtime();

    if (!client) return;

    let subscription: ReturnType<typeof client.private> | null = null;

    try {
      subscription = client.private(channel);

      subscription.listen('.support.message', (payload: { message: SupportMessage }) => {
        // `mine` cannot be broadcast — it depends on who is listening — so it
        // is resolved here from the author, the one thing the server does know.
        absorb([
          {
            ...payload.message,
            mine: isMine ? isMine(payload.message.author) : false,
          },
        ]);

        // Anything arriving means the other side stopped typing to send it.
        setOthersTyping(false);
      });

      subscription.listen('.support.typing', (payload: { side: string }) => {
        // Our own keystrokes come back down the channel. Ignoring them is what
        // stops the chat telling somebody that they are typing.
        const ours = isMine ? isMine(payload.side === 'staff' ? 'staff' : 'buyer') : false;

        if (ours) return;

        setOthersTyping(true);

        // The indicator has to expire on its own: somebody who stops typing
        // sends nothing, and an indicator that lingers says a reply is coming
        // that never is.
        if (typingOff.current) clearTimeout(typingOff.current);
        typingOff.current = setTimeout(() => setOthersTyping(false), 4_000);
      });

      subscription.subscribed?.(() => setLive(true));
      subscription.error?.(() => setLive(false));
    } catch {
      // Nothing to set: `live` starts false and only a successful subscribe
      // turns it on, so a throw here simply leaves the chat on the polling
      // path — which is the correct outcome and the one it was built for.
    }

    return () => {
      if (typingOff.current) clearTimeout(typingOff.current);

      try {
        client.leave(channel);
      } catch {
        /* Already gone. */
      }

      setLive(false);
    };
  }, [channel, absorb, isMine]);

  // ── The loop ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!ready) return;

    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tick = async () => {
      // A hidden tab is not a conversation anybody is reading. Skipping the
      // request rather than clearing the timer means it picks straight back up
      // on return without having to re-arm anything.
      if (typeof document !== 'undefined' && document.hidden) {
        timer = setTimeout(tick, POLL_MS);

        return;
      }

      try {
        const data = await poll(cursor.current);

        if (stopped) return;

        absorb(data.messages);
        setOthersTyping(data.othersTyping);
        setFailed(false);
      } catch {
        // A failed poll is not worth telling anybody about: the next one is
        // two seconds away and the network drops constantly on mobile.
      } finally {
        // Slow while the socket is carrying traffic, quick when it is not.
        if (!stopped) timer = setTimeout(tick, live ? HEARTBEAT_MS : POLL_MS);
      }
    };

    timer = setTimeout(tick, live ? HEARTBEAT_MS : POLL_MS);

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [ready, poll, absorb, live]);

  // ── Keep the newest message in view ──────────────────────────────────────
  useEffect(() => {
    const el = list.current;

    if (!el) return;

    // Only if they were already near the bottom. Yanking somebody back down
    // while they are reading what was said earlier is worse than missing one.
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;

    if (nearBottom) el.scrollTop = el.scrollHeight;
  }, [messages, othersTyping]);

  const onType = (value: string) => {
    setDraft(value);

    const now = Date.now();

    // Throttled hard. One request per keystroke would be a request per
    // keystroke, which is exactly as bad as it sounds.
    if (value.trim() !== '' && now - lastTypingPing.current > TYPING_PING_MS) {
      lastTypingPing.current = now;
      void typing().catch(() => {
        /* A missed typing ping is not worth a retry. */
      });
    }
  };

  const submit = async () => {
    const body = draft.trim();

    if (body === '' || sending) return;

    setSending(true);

    try {
      const message = await send(body);

      absorb([message]);
      setDraft('');
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="live-chat"
      className={cn('flex min-h-0 flex-col overflow-hidden rounded-card border border-line bg-white', className)}
    >
      <div
        ref={list}
        data-testid="chat-messages"
        className="min-h-0 flex-1 space-y-2.5 overflow-y-auto px-3.5 py-4"
      >
        {!ready ? (
          <p className="m-0 text-center text-[12.5px] text-faint">Connecting…</p>
        ) : messages.length === 0 ? (
          <div className="py-6 text-center">{empty}</div>
        ) : (
          messages.map((message) => <Bubble key={message.id} message={message} />)
        )}

        {othersTyping ? (
          <div data-testid="typing" className="flex items-center gap-2 pl-1">
            <Dots />
            <span className="text-[11.5px] text-faint">{otherParty} is typing…</span>
          </div>
        ) : null}
      </div>

      {failed ? (
        <p role="alert" className="m-0 border-t border-line bg-danger-tint px-3.5 py-2 text-[12px] text-danger">
          That did not send. Check your connection and try again.
        </p>
      ) : null}

      <form
        className="flex items-end gap-2 border-t border-line p-2.5"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <label className="min-w-0 flex-1">
          <span className="sr-only">{placeholder}</span>
          <textarea
            value={draft}
            onChange={(event) => onType(event.target.value)}
            onKeyDown={(event) => {
              // Enter sends, shift+enter makes a new line — what every chat
              // does, and what everybody's fingers already expect.
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                void submit();
              }
            }}
            rows={1}
            placeholder={placeholder}
            data-testid="chat-input"
            className="max-h-32 min-h-11 w-full resize-y rounded-btn border border-line bg-white px-3 py-2.5 text-[14px] text-ink outline-none focus:border-blue"
          />
        </label>

        <button
          type="submit"
          disabled={draft.trim() === '' || sending}
          data-testid="chat-send"
          className="grid h-11 w-11 shrink-0 place-items-center rounded-btn bg-blue text-white disabled:opacity-40"
        >
          <span className="sr-only">Send</span>
          <Icon name="chevron" size={18} strokeWidth={2.4} className="-rotate-90" />
        </button>
      </form>
    </div>
  );
}

function Bubble({ message }: { message: SupportMessage }) {
  // A system line is not from either side. It is the app explaining itself,
  // and making it look like a message from support would be a lie about who
  // said it.
  if (message.author === 'system') {
    return (
      <p className="m-0 px-4 py-1 text-center text-[11.5px] leading-snug text-faint">
        {message.body}
      </p>
    );
  }

  return (
    <div className={cn('flex', message.mine ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[82%] rounded-[14px] px-3.5 py-2.5 text-[13.5px] leading-snug',
          message.mine
            ? 'bg-blue text-white'
            : 'border border-line bg-paper text-ink',
        )}
      >
        <p className="m-0 whitespace-pre-wrap break-words">{message.body}</p>

        {message.at ? (
          <time
            dateTime={message.at}
            className={cn(
              'mt-1 block font-mono text-[9.5px] tracking-[0.04em]',
              message.mine ? 'text-white/60' : 'text-faint',
            )}
          >
            {new Date(message.at).toLocaleTimeString('en-NG', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </time>
        ) : null}
      </div>
    </div>
  );
}

/** Three dots. The one piece of motion in the app that earns its place. */
function Dots() {
  return (
    <span aria-hidden className="flex items-center gap-[3px]">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-[5px] w-[5px] rounded-full bg-faint motion-safe:animate-pulse"
          style={{ animationDelay: `${i * 160}ms` }}
        />
      ))}
    </span>
  );
}
